import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";
import { clearDirectoryCache } from "./directory";
import { resolveActiveQueryParam } from "../utils/listActiveFilter";

export type EmploymentType = "full_time" | "daily_paid";

export type Instructor = {
  id: string;
  tenant_id: string;
  branch_id: string;
  user_id: string | null;
  full_name: string;
  phone: string | null;
  email: string | null;
  certificate_expiry_date: string | null;
  employment_type: EmploymentType;
  is_active: boolean;
  weekly_leave_weekday: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
};

export type InstructorListResponse = {
  items: Instructor[];
  total: number;
  limit: number;
  offset: number;
};

export type InstructorInput = {
  branchId?: string;
  userId?: string | null;
  fullName: string;
  phone?: string | null;
  email?: string | null;
  certificateExpiryDate?: string | null;
  employmentType: EmploymentType;
  isActive?: boolean;
  weeklyLeaveWeekday?: number | null;
  notes?: string | null;
};

export type InstructorListSortField =
  | "full_name"
  | "phone"
  | "employment_type"
  | "certificate_expiry_date"
  | "is_active";

export type InstructorListSortDir = "asc" | "desc";

export type InstructorListFilter = {
  q?: string;
  isActive?: boolean | "";
  employmentType?: EmploymentType | "";
  branchId?: string;
  sort?: InstructorListSortField;
  order?: InstructorListSortDir;
  limit?: number;
  offset?: number;
};

export type InstructorListState = {
  data: InstructorListResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
};

export function useInstructorList(filter: InstructorListFilter): InstructorListState {
  const [data, setData] = useState<InstructorListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { q, isActive, employmentType, branchId, sort, order, limit, offset } = filter;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<InstructorListResponse>(
        "/instructors",
        {
          q: q || undefined,
          is_active: resolveActiveQueryParam(isActive),
          employment_type: employmentType || undefined,
          branch_id: branchId || undefined,
          sort: sort || undefined,
          order: order || undefined,
          limit: limit ?? 50,
          offset: offset ?? 0,
        },
        { signal: controller.signal },
      )
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Eğitmenler Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [q, isActive, employmentType, branchId, sort, order, limit, offset, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, reload };
}

export async function createInstructor(input: InstructorInput): Promise<Instructor> {
  const result = await api.post<Instructor>("/instructors", input);
  clearDirectoryCache();
  return result;
}

export async function updateInstructor(id: string, input: InstructorInput): Promise<Instructor> {
  const result = await api.put<Instructor>(`/instructors/${id}`, input);
  clearDirectoryCache();
  return result;
}

export async function setInstructorActive(id: string, isActive: boolean): Promise<Instructor> {
  const result = await api.post<Instructor>(`/instructors/${id}/active`, { isActive });
  clearDirectoryCache();
  return result;
}

export type InstructorLeaveType =
  | "annual"
  | "sick"
  | "excuse"
  | "unpaid"
  | "bad_weather"
  | "eid_fitr"
  | "eid_adha"
  | "public_holiday"
  | "other";

export const INSTRUCTOR_LEAVE_OPTIONS: { value: InstructorLeaveType; label: string }[] = [
  { value: "annual", label: "Yıllık İzin" },
  { value: "sick", label: "Hastalık İzni" },
  { value: "excuse", label: "Mazeret İzni" },
  { value: "unpaid", label: "Ücretsiz İzin" },
  { value: "bad_weather", label: "Olumsuz Hava Koşulları" },
  { value: "eid_fitr", label: "Ramazan Bayramı" },
  { value: "eid_adha", label: "Kurban Bayramı" },
  { value: "public_holiday", label: "Resmi Tatil" },
  { value: "other", label: "Diğer" },
];

export type CreateInstructorLeaveInput = {
  startDate: string;
  endDate: string;
  leaveType: InstructorLeaveType;
  weeklyWeekday?: number | null;
  note?: string | null;
};

export type CreateInstructorLeaveResponse = {
  instructor_id: string;
  days_created: number;
  items: Array<{
    id: string;
    starts_at: string;
    ends_at: string;
    leave_type: string;
  }>;
};

export async function createInstructorLeave(
  instructorId: string,
  input: CreateInstructorLeaveInput,
): Promise<CreateInstructorLeaveResponse> {
  return api.post<CreateInstructorLeaveResponse>(`/instructors/${instructorId}/leaves`, input);
}

export function buildWhatsAppUrl(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D+/g, "");
  if (digits.length === 0) return null;
  let normalized = digits;
  if (normalized.startsWith("00")) normalized = normalized.slice(2);
  if (normalized.startsWith("0")) normalized = `90${normalized.slice(1)}`;
  if (!normalized.startsWith("90") && normalized.length === 10) normalized = `90${normalized}`;
  return `https://wa.me/${normalized}`;
}

export function certificateStatus(date: string | null): {
  daysLeft: number | null;
  tone: "ok" | "soon" | "very-soon" | "expired" | "none";
  label: string;
} {
  if (!date) return { daysLeft: null, tone: "none", label: "Sertifika tarihi yok" };
  const target = new Date(date);
  if (Number.isNaN(target.getTime())) {
    return { daysLeft: null, tone: "none", label: "Geçersiz tarih" };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { daysLeft: diff, tone: "expired", label: `${Math.abs(diff)} Gün once doldu` };
  if (diff <= 14) return { daysLeft: diff, tone: "very-soon", label: `${diff} Gün Kaldı` };
  if (diff <= 60) return { daysLeft: diff, tone: "soon", label: `${diff} Gün Kaldı` };
  return { daysLeft: diff, tone: "ok", label: `${diff} Gün Kaldı` };
}
